import { useCallback, useMemo, type Dispatch, type SetStateAction } from 'react'

import { ListSortMenu } from '@/components/filters/list-sort-menu'
import { ListViewSwitcher } from '@/components/filters/list-view-switcher'
import { VisibleFilterFields } from '@/components/filters/visible-filter-fields'
import type { FilterOption } from '@/components/filters/filter-menu'
import {
  getMemoriesFilterDefinitions,
  MEMORIES_DEFAULT_SORT,
  type MemoriesFilterState,
} from '@/lib/list-filters/memories'
import { MEMORIES_VIEW_OPTIONS, type MemoriesViewMode } from '@/lib/list-view-options'

export type { MemoriesViewMode }

export type UseMemoriesFilterBarOptions = {
  searchTerm: string
  setSearchTerm: (value: string) => void
  categoryFilter: string
  setCategoryFilter: (value: string) => void
  importanceFilter: string
  setImportanceFilter: (value: string) => void
  sortBy: string
  setSortBy: (value: string) => void
  selectedEntity: Array<{ id: string; name: string }>
  setSelectedEntity: Dispatch<SetStateAction<Array<{ id: string; name: string }>>>
  categoryOptions: FilterOption[]
  importanceOptions: FilterOption[]
  sortOptions: FilterOption[]
  viewMode: MemoriesViewMode
  setViewMode: (mode: MemoriesViewMode) => void
  sortDisabled?: boolean
  onPageReset?: () => void
  searchId?: string
}

export function useMemoriesFilterBar({
  searchTerm,
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  importanceFilter,
  setImportanceFilter,
  sortBy,
  setSortBy,
  selectedEntity,
  setSelectedEntity,
  categoryOptions,
  importanceOptions,
  sortOptions,
  viewMode,
  setViewMode,
  sortDisabled = false,
  onPageReset,
  searchId = 'memories-search',
}: UseMemoriesFilterBarOptions) {
  const filterDefinitions = useMemo(() => getMemoriesFilterDefinitions(), [])

  const memoriesFilters: MemoriesFilterState = useMemo(
    () => ({
      searchTerm,
      categoryFilter,
      importanceFilter,
      sortBy,
      selectedEntity,
    }),
    [searchTerm, categoryFilter, importanceFilter, sortBy, selectedEntity],
  )

  const filterCtx = useMemo(
    () => ({
      categoryOptions,
      importanceOptions,
    }),
    [categoryOptions, importanceOptions],
  )

  const onFiltersChange = useCallback(
    (next: MemoriesFilterState) => {
      setCategoryFilter(next.categoryFilter)
      setImportanceFilter(next.importanceFilter)
      setSelectedEntity(next.selectedEntity)
      onPageReset?.()
    },
    [onPageReset, setCategoryFilter, setImportanceFilter, setSelectedEntity],
  )

  const clearFilters = useCallback(() => {
    setCategoryFilter('all')
    setImportanceFilter('all')
    setSelectedEntity([])
    setSearchTerm('')
    setSortBy(MEMORIES_DEFAULT_SORT)
    onPageReset?.()
  }, [
    onPageReset,
    setCategoryFilter,
    setImportanceFilter,
    setSearchTerm,
    setSelectedEntity,
    setSortBy,
  ])

  const hasActiveFilters = useMemo(
    () =>
      searchTerm.trim().length > 0 ||
      sortBy !== MEMORIES_DEFAULT_SORT ||
      filterDefinitions.some((def) => def.isNonDefault(memoriesFilters, filterCtx)),
    [searchTerm, sortBy, filterDefinitions, memoriesFilters, filterCtx],
  )

  const sortMenu = (
    <ListSortMenu
      options={sortOptions}
      value={sortBy}
      onValueChange={(value) => {
        setSortBy(value)
        onPageReset?.()
      }}
      ariaLabel={sortDisabled ? 'Sort memories (list view only)' : 'Sort memories'}
      disabled={sortDisabled}
    />
  )

  const filterBar = (
    <VisibleFilterFields
      listId="memories"
      filters={memoriesFilters}
      onFiltersChange={onFiltersChange}
      definitions={filterDefinitions}
      ctx={filterCtx}
      toolbar={{
        viewSwitcher: (
          <ListViewSwitcher
            value={viewMode}
            onValueChange={setViewMode}
            options={MEMORIES_VIEW_OPTIONS}
            ariaLabel="Memories view"
          />
        ),
        sortMenu,
      }}
      search={{
        searchId,
        searchPlaceholder: 'Search memories…',
        searchValue: searchTerm,
        onSearchChange: (value) => {
          setSearchTerm(value)
          onPageReset?.()
        },
      }}
    />
  )

  return { filterBar, hasActiveFilters, clearFilters }
}
